import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Building, ArrowLeft, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { usePricingSaasData } from './hooks/usePricingSaasData';
import { useManagementAPI } from '@/hooks/useManagementAPI';
import { PageSelector } from './components/PageSelector';
import { PricingCardsDisplay } from './components/PricingCardsDisplay';
import { ErrorState } from './components/ErrorState';
import { RunExtractButton } from './components/RunExtractButton';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// JSON Viewer Component with syntax highlighting
interface JsonViewerProps {
  data: unknown;
  title?: string;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ data, title = 'Raw JSON' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple syntax highlighting
  const highlightJson = (json: string) => {
    return json
      .replace(/(".*?")\s*:/g, '<span class="text-purple-600">$1</span>:') // keys
      .replace(/:\s*(".*?")/g, ': <span class="text-green-600">$1</span>') // string values
      .replace(/:\s*(\d+\.?\d*)/g, ': <span class="text-blue-600">$1</span>') // numbers
      .replace(/:\s*(true|false)/g, ': <span class="text-amber-600">$1</span>') // booleans
      .replace(/:\s*(null)/g, ': <span class="text-gray-400">$1</span>'); // null
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors text-left">
            <div className="flex items-center gap-2">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-200">{title}</span>
              <span className="text-xs text-gray-500">
                ({Object.keys(data as object || {}).length} keys)
              </span>
            </div>
            {isOpen && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                className="h-7 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                <span className="ml-1.5 text-xs">{copied ? 'Copied!' : 'Copy'}</span>
              </Button>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="max-h-[500px] overflow-auto">
            <pre
              className="p-4 text-xs font-mono text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: highlightJson(jsonString) }}
            />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const CompanyPlansPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();

  const {
    company,
    loading: companyLoading,
  } = useManagementAPI(slug || '');

  const {
    data,
    loading: plansLoading,
    error,
    selectedPageIndex,
    setSelectedPageIndex,
    refetch
  } = usePricingSaasData(slug || '');

  // Format company name from slug
  const companyName = slug
    ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    : '';

  // Handler for running extract from ErrorState
  const handleRunExtract = async () => {
    if (!slug) return;

    try {
      const { error: insertError } = await supabase
        .from('claude_tasks')
        .insert({
          command: 'run_extract',
          slug: slug,
          params: { slug },
          status: 'pending',
          priority: 5,
        });

      if (insertError) {
        throw insertError;
      }

      toast({
        title: 'Extract task created',
        description: `Extraction queued for "${slug}"`,
      });
    } catch (err) {
      console.error('Error creating extract task:', err);
      toast({
        title: 'Error creating task',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  if (!slug) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6">
          <div className="text-center text-gray-500">
            No company slug provided
          </div>
        </div>
      </div>
    );
  }

  const currentPage = data?.pages[selectedPageIndex];
  const loading = companyLoading || plansLoading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full px-6 py-4">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/companies">Companies</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/edit/${slug}`}>{companyName}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Plans</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Compact Company Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to={`/edit/${slug}`}>
              <Button variant="ghost" size="sm" className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            {company?.logo_url ? (
              <img
                src={company.logo_url}
                alt={`${companyName} logo`}
                className="w-10 h-10 object-contain rounded border border-gray-100"
              />
            ) : (
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                <Building className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{companyName}</h1>
              <p className="text-sm text-gray-500">Pricing Plans</p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              {data?.pages && data.pages.length > 1 && (
                <PageSelector
                  pages={data.pages}
                  currentIndex={selectedPageIndex}
                  onChange={setSelectedPageIndex}
                />
              )}
              <RunExtractButton slug={slug} />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-8">
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
              <span className="text-lg text-gray-600">Loading pricing data...</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-80 w-full rounded-lg" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <ErrorState error={error} onRetry={refetch} onRunExtract={handleRunExtract} />
        )}

        {/* Content */}
        {!loading && !error && data && currentPage && (
          <div className="space-y-6">
            {/* Plans Display */}
            {currentPage.plans && currentPage.plans.length > 0 ? (
              <PricingCardsDisplay
                plans={currentPage.plans}
                pageMetrics={currentPage.pricing_metric}
              />
            ) : (
              <div className="text-center py-20">
                <div className="text-gray-400 text-lg mb-4">No pricing plans available for this page</div>
                <RunExtractButton slug={slug} variant="default" />
              </div>
            )}

            {/* Page Metadata Footer */}
            {currentPage.version && (
              <div className="mt-16 pt-8 border-t border-gray-200 text-center">
                <div className="text-sm text-gray-500">
                  <span className="font-medium">Extracted:</span>{' '}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help underline decoration-dotted">
                        {currentPage.version}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Version: {currentPage.version}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {currentPage.scrape?.url && (
                  <div className="mt-2">
                    <a
                      href={currentPage.scrape.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                    >
                      View Original Source →
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Raw JSON Data */}
            <div className="mt-12">
              <JsonViewer data={currentPage} title="Raw Page Data" />
            </div>
          </div>
        )}

        {/* No Data State */}
        {!loading && !error && !data && (
          <div className="text-center py-20">
            <div className="text-gray-400 text-lg mb-4">
              No pricing information available for this company
            </div>
            <RunExtractButton slug={slug} variant="default" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPlansPage;
